import React, { useState, useEffect, useContext } from "react";
import "./EditReportSketchDesign.css";
import {
  getSketchDesignReportsByUser,
  updateSketchDesignReport,
} from "../../../api/reportsApi";
import { getSketchDesignProjects } from "../../../api/projectApi";
import { getSketchDesignEstimateWorkTypes } from "../../../api/projectEstimatesApi";
import { UserContext } from "../../../contexts/CurrentUserContext";
import Preloader from "../../Preloader/Preloader";
import EditReportSketchDesignModal from "./EditReportSketchDesignModal";

const formatSecondsSpend = (secondsSpend) => {
  if (secondsSpend == null) return "-";
  const totalMinutes = Math.floor(Number(secondsSpend) / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const EditReportSketchDesign = () => {
  const { userData } = useContext(UserContext);
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workTypesByProject, setWorkTypesByProject] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupError, setPopupError] = useState(null);

  const userId = userData?.id;

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [reportsData, projectsData] = await Promise.all([
          getSketchDesignReportsByUser(userId),
          getSketchDesignProjects(),
        ]);

        if (cancelled) return;

        const list = Array.isArray(reportsData) ? reportsData : [];
        setReports(
          [...list].sort(
            (a, b) => new Date(b.datetime || 0) - new Date(a.datetime || 0)
          )
        );
        setProjects(Array.isArray(projectsData) ? projectsData : []);

        const projectIds = [
          ...new Set(list.map((r) => r.project_id).filter(Boolean)),
        ];
        const wtMap = {};
        await Promise.all(
          projectIds.map(async (pid) => {
            try {
              const raw = await getSketchDesignEstimateWorkTypes(pid);
              const wts = Array.isArray(raw) ? raw : raw?.work_types ?? [];
              wtMap[pid] = Array.isArray(wts) ? wts : [];
            } catch {
              wtMap[pid] = [];
            }
          })
        );
        if (!cancelled) setWorkTypesByProject(wtMap);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Ошибка загрузки отчётов");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId]);

  const getProjectName = (projectId) => {
    const p = projects.find((x) => x.id === projectId);
    return p?.name ?? projectId ?? "-";
  };

  const getWorkTypeName = (projectId, workTypeId) => {
    const wts = workTypesByProject[projectId] || [];
    const wt = wts.find((x) => x.id === workTypeId);
    return wt?.name ?? workTypeId ?? "-";
  };

  const handleReportClick = (report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
    setPopupError(null);
  };

  const handleSaveReport = async (payload) => {
    if (!selectedReport?.id) return;
    try {
      await updateSketchDesignReport(selectedReport.id, payload);

      const [reportsData, projectsData] = await Promise.all([
        getSketchDesignReportsByUser(userId),
        getSketchDesignProjects(),
      ]);
      const list = Array.isArray(reportsData) ? reportsData : [];
      setReports(
        [...list].sort(
          (a, b) => new Date(b.datetime || 0) - new Date(a.datetime || 0)
        )
      );
      setProjects(Array.isArray(projectsData) ? projectsData : []);

      const projectIds = [
        ...new Set(list.map((r) => r.project_id).filter(Boolean)),
      ];
      const wtMap = {};
      await Promise.all(
        projectIds.map(async (pid) => {
          try {
            const raw = await getSketchDesignEstimateWorkTypes(pid);
            const wts = Array.isArray(raw) ? raw : raw?.work_types ?? [];
            wtMap[pid] = Array.isArray(wts) ? wts : [];
          } catch {
            wtMap[pid] = [];
          }
        })
      );
      setWorkTypesByProject(wtMap);

      handleCloseModal();
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 3000);
    } catch (err) {
      setPopupError(err?.message || "Ошибка сохранения");
      throw err;
    }
  };

  if (loading && reports.length === 0) {
    return <Preloader />;
  }

  if (error) {
    return (
      <div className="edit-report-sketch error-message">
        Ошибка при загрузке отчётов: {error}
      </div>
    );
  }

  return (
    <div className="edit-report-sketch">
      <h2 className="edit-report-sketch__title">
        Редактирование отчётов (эскизное отделение)
      </h2>
      {reports.length === 0 ? (
        <p className="edit-report-sketch__empty">
          Нет отчётов за текущий период
        </p>
      ) : (
        <table className="edit-report-sketch-table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Проект</th>
              <th>Часы</th>
              <th>Вид работ</th>
              <th>Комментарий</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr
                key={report.id}
                onClick={() => handleReportClick(report)}
                className={selectedReport?.id === report.id ? "selected" : ""}
              >
                <td>
                  {report.datetime
                    ? new Date(report.datetime).toLocaleDateString("ru-RU", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                    : "-"}
                </td>
                <td>{getProjectName(report.project_id)}</td>
                <td>{formatSecondsSpend(report.seconds_spend)}</td>
                <td>
                  {getWorkTypeName(report.project_id, report.work_type_id)}
                </td>
                <td>{report.comment || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isModalOpen && selectedReport && (
        <EditReportSketchDesignModal
          report={selectedReport}
          projects={projects}
          workTypesByProject={workTypesByProject}
          onClose={handleCloseModal}
          onSave={handleSaveReport}
          error={popupError}
        />
      )}

      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className={`popup ${popupError ? "popup-error" : ""}`}>
            <div
              className={`popup-icon ${popupError ? "popup-icon-error" : ""}`}
            />
            <p className="popup-text">
              {popupError || "Отчёт успешно обновлён"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditReportSketchDesign;
